/**
 * useSocket Hook
 *
 * Custom hook for managing Socket.io connections with automatic reconnection
 */

import { selectCurrentUser } from "@/services/slices/authSlice";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io, Socket } from "socket.io-client";

interface UseSocketOptions {
  namespace?: string;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  /**
   * Force une connexion neuve (pas de multiplexage/cache socket.io). Nécessaire
   * pour la présence : au changement de compte, on doit se reconnecter avec la
   * nouvelle identité plutôt que réutiliser le socket caché de l'ancienne.
   */
  forceNew?: boolean;
  /**
   * Nombre de tentatives de reconnexion (défaut 5). La présence passe Infinity
   * pour qu'après un redéploiement de l'API les clients se reconnectent seuls
   * et resynchronisent la liste sans reload manuel.
   */
  reconnectionAttempts?: number;
}

interface UseSocketReturn {
  socket: Socket | null;
  connected: boolean;
  connecting: boolean;
  error: Error | null;
  connect: () => void;
  disconnect: () => void;
}

/**
 * Hook to manage Socket.io connection to a specific namespace
 *
 * @param namespace - Socket.io namespace (e.g., '/notifications', '/data', '/activity')
 * @param options - Configuration options
 * @returns Socket instance and connection state
 */
export function useSocket(
  namespace: string = "/",
  options: UseSocketOptions = {}
): UseSocketReturn {
  const {
    onConnect,
    onDisconnect,
    onError,
    forceNew = false,
    reconnectionAttempts = 5,
  } = options;

  const user = useSelector(selectCurrentUser);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use ref to store callbacks to avoid re-creating socket on callback changes
  const callbacksRef = useRef({ onConnect, onDisconnect, onError });

  useEffect(() => {
    callbacksRef.current = { onConnect, onDisconnect, onError };
  }, [onConnect, onDisconnect, onError]);

  useEffect(() => {
    if (user) connect();
  }, [user]);

  const connect = useCallback(() => {
    console.log("-------");

    if (socket?.connected) {
      console.warn("Socket already connected");
      return;
    }

    setConnecting(true);
    setError(null);

    const apiUri = process.env.REACT_APP_API_URI || "http://localhost:3333";

    if (!user) {
      const err = new Error("No user found");
      setError(err);
      setConnecting(false);
      callbacksRef.current.onError?.(err);
      console.warn("[useSocket] No user found - user may not be logged in yet");
      return;
    }

    console.log("[useSocket] Connecting to:", `${apiUri}${namespace}`);

    const socketInstance = io(`${apiUri}${namespace}`, {
      auth: { userId: user.id },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts,
      forceNew,
    });

    socketInstance.on("connect", () => {
      console.log(`[Socket] Connected to ${namespace}`, {
        socketId: socketInstance.id,
      });
      setConnected(true);
      setConnecting(false);
      setError(null);
      callbacksRef.current.onConnect?.();
    });

    socketInstance.on("disconnect", (reason) => {
      console.log(`[Socket] Disconnected from ${namespace}`, { reason });
      setConnected(false);
      setConnecting(false);
      callbacksRef.current.onDisconnect?.(reason);
    });

    socketInstance.on("connect_error", (err) => {
      console.error(`[Socket] Connection error on ${namespace}`, err);
      setError(err as Error);
      setConnecting(false);
      callbacksRef.current.onError?.(err as Error);
    });

    socketInstance.on("error", (err) => {
      console.error(`[Socket] Error on ${namespace}`, err);
      setError(new Error(err));
      callbacksRef.current.onError?.(new Error(err));
    });

    setSocket(socketInstance);
    setConnecting(true);
  }, [namespace, socket, user, forceNew, reconnectionAttempts]);

  const disconnect = useCallback(() => {
    if (socket) {
      socket.close();
      setSocket(null);
      setConnected(false);
      setConnecting(false);
    }
  }, [socket]);

  return {
    socket,
    connected,
    connecting,
    error,
    connect,
    disconnect,
  };
}
